// The pure FOLLOW/BROWSE/SEEK decider for the video source view.
// .claude/analysis/video-source-view-synced-transcript.md §2 is the spec this
// realises verbatim (state sum, event sum, transition table §2.3).
//
// Zero I/O: `decide` never touches the DOM, the `<video>` element, or a
// clock. It is a pure function of (state, event, lines) to (next state,
// effects-to-perform) — the same decider/evolve shape used elsewhere in this
// codebase, scoped to view-local UI state rather than the event-sourced
// domain (§2.2 of the doc: ephemeral, but still pure). `SyncedTranscriptRenderer`
// is the impure shell: it owns the `<video>` element, the `timeupdate` and
// `scroll` listeners, and performs whatever effects `decide` returns.
//
// One deliberate deviation from the doc's literal signature, worth stating:
// the doc's `SyncEffect` sum includes a `NoEffect` leaf, and its transition
// table has ONE transition (`LineClicked`) that produces TWO effects at once
// (seek + scroll). Modelling that with a single `effect: SyncEffect` field
// would need either a third "SeekAndScroll" leaf (a combinator that isn't a
// genuinely distinct effect, just two existing ones fired together) or an
// awkward union-of-tuples. Simpler: `decide` returns `effects: readonly
// SyncEffect[]`. "No effect" is then the empty array, not its own variant —
// absence of an effect is not itself an effect (same reasoning as dropping a
// boolean flag in favour of a real sum: don't model "nothing happened" as a
// case of "something happened").

import { exhaustSwitchCase } from "@bilda/utils";

export type {
    Browse,
    DecideResult,
    Follow,
    LineClicked,
    LineId,
    PlayerTimeAdvanced,
    ResyncRequested,
    ScrollTranscriptTo,
    Seconds,
    SeekVideoTo,
    SyncEffect,
    SyncEvent,
    SyncState,
    TranscriptLine,
    UserScrolledTranscript,
};
export { decide, lineAt, linesFromCues, mkInitialSyncState };

import type { VttCue } from "./vtt.js";

type Seconds = number;
type LineId = string;

// One transcript line — the sync unit. Parallels `.claude/analysis/…§3`'s
// `Target { selectors: [TemporalSelector { start, end }] }`: a line IS a
// temporal selector over the video blob, with display text attached.
type TranscriptLine = {
    readonly id: LineId;
    readonly start: Seconds;
    readonly end: Seconds;
    readonly text: string;
};

// === State (§2.2) ============================================================

type Follow = { readonly type: "follow"; readonly currentLineId: LineId };
type Browse = { readonly type: "browse"; readonly currentLineId: LineId };
type SyncState = Follow | Browse;

// === Events (§2.2) ===========================================================

type PlayerTimeAdvanced = {
    readonly type: "player_time_advanced";
    readonly time: Seconds;
};
type UserScrolledTranscript = { readonly type: "user_scrolled_transcript" };
type LineClicked = { readonly type: "line_clicked"; readonly lineId: LineId };
type ResyncRequested = { readonly type: "resync_requested" };
type SyncEvent =
    | PlayerTimeAdvanced
    | UserScrolledTranscript
    | LineClicked
    | ResyncRequested;

// === Effects (§2.2) — see the module comment for why `NoEffect` is dropped ==

type SeekVideoTo = { readonly type: "seek_video_to"; readonly time: Seconds };
type ScrollTranscriptTo = {
    readonly type: "scroll_transcript_to";
    readonly lineId: LineId;
    readonly behavior: "smooth" | "auto";
};
type SyncEffect = SeekVideoTo | ScrollTranscriptTo;

type DecideResult = {
    readonly state: SyncState;
    readonly effects: readonly SyncEffect[];
};

// The line whose `[start, end)` contains `time`. `lines` must be ordered
// ascending by `start` (the parse/adapter's job, not this function's). Between
// two cues' end/start gap, the PREVIOUS line is still considered current —
// captions commonly have small gaps, and dropping the pin to "nothing" every
// gap would flicker the UI. Before the first cue starts, there is no current
// line yet: `null`.
const lineAt = (
    time: Seconds,
    lines: readonly TranscriptLine[],
): LineId | null => {
    let current: LineId | null = null;
    for (const line of lines) {
        if (line.start > time) break;
        current = line.id;
    }
    return current;
};

// The transition table, §2.3 of the doc, verbatim:
//
// | State        | Event                              | New state    | Effect(s) |
// |--------------|-------------------------------------|--------------|-----------|
// | Follow{L}    | PlayerTimeAdvanced, lineAt==L       | Follow{L}    | none (boundary-crossing guard) |
// | Follow{L}    | PlayerTimeAdvanced, lineAt==L2      | Follow{L2}   | ScrollTranscriptTo(L2, smooth) |
// | Follow{L}    | UserScrolledTranscript              | Browse{L}    | none (the scroll already happened, shell-side) |
// | Browse{L}    | PlayerTimeAdvanced, lineAt==L2      | Browse{L2}   | none (track live line, never auto-scroll) |
// | Browse{L}    | ResyncRequested                     | Follow{L}    | ScrollTranscriptTo(L, smooth) |
// | Follow\|Browse{L} | LineClicked(L2)                | Follow{L2}   | SeekVideoTo(L2.start) + ScrollTranscriptTo(L2, auto) |
//
// Two transitions not in the doc's table, filled in for totality:
// `Follow{L}` + `ResyncRequested` is a no-op (already following); `Browse{L}`
// + `UserScrolledTranscript` is a no-op (already browsing, idempotent).
//
// Reference equality: when a branch has nothing to change, it returns the
// SAME `state` object it was given (not a freshly-built equal one) so a
// caller wired through `useState`/`useReducer` bails out the re-render for
// free (§2.5 "Follow → Follow no-op transitions produce no re-render").
const decide = (
    state: SyncState,
    event: SyncEvent,
    lines: readonly TranscriptLine[],
): DecideResult => {
    switch (event.type) {
        case "player_time_advanced": {
            const next = lineAt(event.time, lines) ?? state.currentLineId;
            if (next === state.currentLineId) return { state, effects: [] };
            if (state.type === "follow") {
                return {
                    state: { type: "follow", currentLineId: next },
                    effects: [
                        {
                            type: "scroll_transcript_to",
                            lineId: next,
                            behavior: "smooth",
                        },
                    ],
                };
            }
            return {
                state: { type: "browse", currentLineId: next },
                effects: [],
            };
        }
        case "user_scrolled_transcript":
            return state.type === "follow"
                ? {
                      state: {
                          type: "browse",
                          currentLineId: state.currentLineId,
                      },
                      effects: [],
                  }
                : { state, effects: [] };
        case "resync_requested":
            return state.type === "browse"
                ? {
                      state: {
                          type: "follow",
                          currentLineId: state.currentLineId,
                      },
                      effects: [
                          {
                              type: "scroll_transcript_to",
                              lineId: state.currentLineId,
                              behavior: "smooth",
                          },
                      ],
                  }
                : { state, effects: [] };
        case "line_clicked": {
            const clicked = lines.find((line) => line.id === event.lineId);
            if (!clicked) return { state, effects: [] };
            return {
                state: { type: "follow", currentLineId: clicked.id },
                effects: [
                    { type: "seek_video_to", time: clicked.start },
                    {
                        type: "scroll_transcript_to",
                        lineId: clicked.id,
                        behavior: "auto",
                    },
                ],
            };
        }
        default:
            return exhaustSwitchCase(event);
    }
};

// The decider's starting state: `Follow` on the first line. `null` for an
// empty transcript — the renderer's caller is expected to have already
// degraded to the `Flat`/`EmptyState` floor (§5) before mounting the synced
// view at all, so this is a total, honest signal rather than a thrown error.
const mkInitialSyncState = (
    lines: readonly TranscriptLine[],
): SyncState | null => {
    const first = lines[0];
    return first ? { type: "follow", currentLineId: first.id } : null;
};

// Bridge from the VTT parser's vocabulary (§4 ingest codec) to the decider's
// (§2.2/§3 — a line is a `TemporalSelector`-anchored unit). Kept here, not in
// `vtt.ts`, so the parser stays ignorant of the sync decider entirely — it
// only knows WebVTT syntax.
const linesFromCues = (cues: readonly VttCue[]): readonly TranscriptLine[] =>
    cues.map((cue) => ({
        id: `line-${cue.index}`,
        start: cue.start,
        end: cue.end,
        text: cue.text,
    }));
